import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Point } from 'geojson';
import { firstValueFrom, from, ReplaySubject, Subject } from 'rxjs';
import { find, map, pluck, switchMap, takeUntil } from 'rxjs/operators';
import { ApiGatewayService } from '~app/shared/api-gateway.service';
import { MapElement } from '~app/shared/map/map-element.model';
import { MapService } from '~app/shared/map/map.service';
import { TAnalyzePointsResult, Tv2l } from './v2l.type';
import distance from '@turf/distance';

@Injectable()
export class V2LService extends ApiGatewayService implements OnDestroy {
  private readonly ngUnsubscribe$ = new Subject<void>();
  private readonly analysis$ = new ReplaySubject<TAnalyzePointsResult>(1);
  private analyzesInitialized = false;

  constructor(
    protected http: HttpClient,
  ) {
    super(http, 'v2l-api');
    console.log('V2LService default constructor');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  /**
   * Query and draw V2L distinct coordinate points to canvas
   * @param ms MapService reference
   */
  public initMapItems(ms: MapService) {
    console.log('V2LService initMapItems');

    //// eagerly fetch data, if not yet fetched
    ms.layersR$.pipe(
      find(layer => layer.metadata.v2lLayer),
      takeUntil(this.ngUnsubscribe$)
    ).subscribe( (v2lLayer) => {
      if (!this.analyzesInitialized) {
        from(this.getDistinctCoordinates()).subscribe({
          next: (points: TAnalyzePointsResult) => {
            this.analysis$.next(points);
          }
        });
        this.analyzesInitialized = true;
      }
    });

    //// add items to layer
    this.analysis$.pipe(
      takeUntil(this.ngUnsubscribe$)
    ).subscribe( (points) => {
      //// at moment, we have single v2l layer for tenant, if configuration error (multiple v2l layers defined) picking the first one
      const v2lLayer = ms.getLayerList().find((layer) => layer.metadata.v2lLayer);
      const elements = points.data.map(point => {
        //// generate 8 characters long id for v2l items from the coordinate pair
        const decimalSum = point.coordinates[0]*point.coordinates[1];
        const hashedId = (decimalSum.toFixed(5).toString()).slice(-8);

        return new MapElement<Point>(hashedId, v2lLayer, {id: hashedId}, 'Feature', point);
      });
      if (v2lLayer) {
        console.log(`adding items on layer ${v2lLayer.id}`);
        v2lLayer.pushFeatures(elements);
      }
    });
  }

  /**
   * fetch distinct coordinate pairs (Point) from V2L
   * NOTE: let this be, as long as the other endpoints get stabilized.
   * @returns array of distinct Points
   */
  public getDistinctCoordinates(): Promise<TAnalyzePointsResult> {
    return this.get(['v2l-egress', 'distinct', 'NaytteenottoMatka.Naytteenotto.NaytteenottoPaikka.geometry']);
  }

  /**
   * analysis for specific coordinates
   * @param point
   * @returns
   */
  public getLatestAnalysisForCoordinates(point: Point): Promise<{data: [Tv2l]}> {
    return this.get(['v2l-egress', 'filter'], {
      x: `${point.coordinates[0]}`,
      y: `${point.coordinates[1]}`,
      maxDistance: '5', //// should be exact match!
      limit: '1',
      sort: `{'updatedAt': -1}`
    });
  }

  /**
   * find out coordinate match between clicked point and v2l laboratory analysis coordinates
   * return 'original' v2l geojson point if decent match is found
   * @param feature
   * @returns
   */
  public getHasAnalysis(feature: MapElement<Point, any>): Promise<Point | undefined> {
    return firstValueFrom(this.analysis$.pipe(
      takeUntil(this.ngUnsubscribe$),
      pluck('data'),
      map(pps => pps.find(point => {
        //// rego feature have some offset
        //// accuracy of the clicked feature coordinates depends on map zoom level!
        if (point.coordinates === feature.geometry.coordinates) {
          console.log('match found with direct coordinate comparison');
          return true;
        }
        if (distance(point, feature.geometry, {units: 'meters'}) < 10) {
          console.log('match found with distance search');
          return true;
        }
      }))
    ));
  }
}
